import { explosionAction, type EmoteId, type ProjectileKind } from '@tanky/shared';
import { AudioEngine, type Listener3D, type OutputMode, type Voice } from './audio/engine';

const STORAGE_KEY = 'tanky.sound';
const VOLUME_KEY = 'tanky.sound.volume';
const OUTPUT_KEY = 'tanky.sound.output';

function load(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function save(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* úložiště nemusí být dostupné */
  }
}

/**
 * Zvuky hry: přehrávají se soubory z knihovny zvuků (stránka Zvuky), které
 * jsou přiřazené k herním akcím. Výstup je prostorový 5.1, případně smíchaný
 * do sterea. Akce bez přiřazeného zvuku je potichu.
 */
class Sound {
  readonly engine = new AudioEngine();
  private listeners = new Set<(on: boolean) => void>();
  enabled: boolean;

  constructor() {
    this.enabled = load(STORAGE_KEY) !== 'off';
    const vol = Number(load(VOLUME_KEY));
    if (load(VOLUME_KEY) !== null && Number.isFinite(vol)) this.engine.volume = Math.max(0, Math.min(1, vol));
    const out = load(OUTPUT_KEY);
    if (out === 'auto' || out === 'surround' || out === 'stereo') this.engine.output = out;
    // Prohlížeče pustí zvuk až po první interakci uživatele
    const unlock = () => {
      if (!this.enabled) return;
      this.engine.resume();
      void this.engine.library.load();
    };
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('keydown', unlock);
  }

  setEnabled(on: boolean) {
    this.enabled = on;
    save(STORAGE_KEY, on ? 'on' : 'off');
    if (on) this.engine.resume();
    else this.engine.suspend();
    for (const l of this.listeners) l(on);
  }

  toggle() {
    this.setEnabled(!this.enabled);
  }

  onChange(l: (on: boolean) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  setVolume(v: number) {
    this.engine.setVolume(v);
    save(VOLUME_KEY, String(this.engine.volume));
  }

  setOutput(mode: OutputMode) {
    this.engine.setOutput(mode);
    save(OUTPUT_KEY, mode);
  }

  /** 2D: kam se „dívá“ kamera (střed a polovina šířky záběru ve světě). */
  setListener(worldX: number, worldY: number, halfSpan: number) {
    this.engine.setListener2D(worldX, worldY, halfSpan);
  }

  /** Kde hráč slyší – poloha jeho tanku (null = střed záběru, např. divák). */
  setEar(p: { x: number; y: number } | null) {
    this.engine.setEar(p);
  }

  /** 3D: poloha a směr kamery. */
  setListener3D(l: Listener3D) {
    this.engine.setListener3D(l);
  }

  private play(action: string, x?: number, opts: { y?: number; z?: number; volume?: number; duration?: number } = {}): Voice | null {
    if (!this.enabled) return null;
    return this.engine.play(action, { x, ...opts });
  }

  // ---------- Herní akce ----------

  fire(kind: ProjectileKind, x?: number) {
    this.play(`fire.${kind}`, x);
  }

  explosion(r: number, x?: number, y?: number) {
    this.play(explosionAction(r), x, { y });
  }

  dirt(x?: number) {
    this.play('explosion.dirt', x);
  }

  marker(x?: number) {
    this.play('marker', x);
  }

  /** Motor řízené střely – hraje, dokud se nezavolá vrácená funkce. */
  engineLoop(x?: number, y?: number): Voice | null {
    return this.play('engine', x, { y });
  }

  mineBeep(x?: number) {
    this.play('mine.beep', x);
  }

  blackhole(x?: number) {
    this.play('blackhole', x);
  }

  thunder(x?: number) {
    this.play('thunder', x);
  }

  quake() {
    this.play('quake');
  }

  /** Hvízdání padající bomby. */
  whistle(x?: number) {
    this.play('whistle', x);
  }

  splash(x?: number) {
    this.play('splash', x);
  }

  /** Sesuv půdy – hlasitost podle množství zeminy. */
  slide(amount: number, x?: number) {
    if (amount < 30) return;
    this.play('slide', x, { volume: 0.35 + Math.min(1, amount / 3000) * 0.65 });
  }

  /** Hořící napalm (smyčka na daný počet sekund). */
  burn(x?: number, seconds = 3) {
    this.play('burn', x, { duration: seconds });
  }

  teleport(x?: number) {
    this.play('teleport', x);
  }

  hit(x?: number) {
    this.play('hit', x);
  }

  death(x?: number) {
    this.play('death', x);
  }

  /** Rotory dronu (smyčka, poloha se posouvá). */
  drone(x: number, y: number, z: number): Voice | null {
    return this.play('drone', x, { y, z });
  }

  yourTurn() {
    this.play('your_turn');
  }

  chat() {
    this.play('chat');
  }

  win() {
    this.play('win');
  }

  emote(kind: EmoteId, x?: number) {
    this.play(`emote.${kind}`, x);
  }
}

export const sound = new Sound();
